import { useMutation } from "@tanstack/react-query";
import type { MemoSummary } from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@portal/ui";
import {
  Archive,
  ArchiveRestore,
  EllipsisVertical,
  FileText,
  Pin,
  PinOff,
  Trash2,
  Users,
} from "lucide-react";
import { useState } from "react";
import { deleteMemo, updateMemoArchived, updateMemoPinned } from "../../api/client";
import { useInvalidate, useMemoDetail } from "../../api/queries";
import { InlineError } from "../../components/InlineError";
import { MemoIcon } from "./MemoIcon";
import { MemoShareDialog } from "./MemoShareDialog";

/**
 * メモの一覧の行の「⋮」メニュー(ユーザー要望 2026-10-15)。行にカーソルを乗せると出る。
 * 開く・共有・アーカイブ(元に戻す)・削除。共有・アーカイブ・削除は所有者にだけ出す。
 * プロジェクトのメモ(§13.6)には共有が無く、代わりに参加者へ「ボードに留める・外す」を出す。
 */
export function MemoRowMenu({
  memo,
  onOpen,
  canPin = false,
}: {
  memo: MemoSummary;
  onOpen: () => void;
  /** ボードに留められるか(プロジェクトのメモで、自分が参加者か作った人)。 */
  canPin?: boolean;
}) {
  const isProject = memo.projectId !== null;
  const invalidate = useInvalidate();
  const [sharing, setSharing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const owner = memo.role === "OWNER";
  const title = memo.title || "無題のメモ";
  // 共有の中身(誰に共有しているか)は詳細にしか無いので、共有を開くときに取る
  const detail = useMemoDetail(sharing ? memo.id : null);
  const archive = useMutation({
    mutationFn: (archived: boolean) =>
      updateMemoArchived(memo.id, { archived, revision: memo.revision }),
    onSuccess: () => invalidate.memos(),
  });
  const pin = useMutation({
    mutationFn: (pinned: boolean) => updateMemoPinned(memo.id, { pinned, revision: memo.revision }),
    onSuccess: () => invalidate.memos(),
  });
  const remove = useMutation({
    mutationFn: () => deleteMemo(memo.id),
    onSuccess: () => {
      setConfirming(false);
      void invalidate.memoDeleted(memo.id);
    },
  });

  return (
    <div className="flex shrink-0 flex-col items-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            // 題名を入れない(行の「開く」ボタンの名前と紛れないように。行の中にあるので、どのメモかは分かる)
            aria-label="メモの操作"
            // 行にカーソルが乗ったとき・キーボードで来たとき・開いているときだけ見せる
            className="shrink-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          >
            <EllipsisVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* どのメモの操作かが分かるよう、先頭に題名を出す(ユーザー要望 2026-10-15) */}
          <DropdownMenuLabel className="flex items-center gap-2 font-medium">
            <MemoIcon value={memo.icon} />
            <span className="truncate">{title}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={onOpen}>
            <FileText />
            開く
          </DropdownMenuItem>
          {canPin && !memo.archived && (
            <DropdownMenuItem onSelect={() => pin.mutate(memo.pinnedAt === null)}>
              {memo.pinnedAt === null ? <Pin /> : <PinOff />}
              {memo.pinnedAt === null ? "ボードに留める" : "ボードから外す"}
            </DropdownMenuItem>
          )}
          {owner && (
            <>
              {!isProject && (
                <DropdownMenuItem onSelect={() => setSharing(true)}>
                  <Users />
                  共有
                </DropdownMenuItem>
              )}
              {memo.archived ? (
                <DropdownMenuItem onSelect={() => archive.mutate(false)}>
                  <ArchiveRestore />
                  元に戻す
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => archive.mutate(true)}>
                  <Archive />
                  アーカイブ
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setConfirming(true)}>
                <Trash2 />
                削除
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <InlineError error={archive.error ?? pin.error} />
      {detail.data && (
        <MemoShareDialog memo={detail.data.memo} open={sharing} onOpenChange={setSharing} />
      )}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>「{title}」を削除しますか?</DialogTitle>
            <DialogDescription>
              {isProject
                ? "プロジェクトの全員から見えなくなります。元に戻せません(残すならアーカイブ)。"
                : "共有している人からも見えなくなります。元に戻せません(残すならアーカイブ)。"}
            </DialogDescription>
          </DialogHeader>
          <InlineError error={remove.error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirming(false)}>
              キャンセル
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
